import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import ProfilePictureUpload from "../components/ProfilePictureUpload";
import {
  Zap,
  Building2,
  MapPin,
  Globe,
  Users,
  Briefcase,
  Mail,
  Phone,
  Save,
  Edit,
  Plus,
  X,
  Check,
  User
} from "lucide-react";

const RecruiterProfile = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    company_description: "",
    company_website: "",
    company_size: "",
    industry: "",
    location: "",
    phone: "",
    hiring_for: [],
    company_logo: "",
    company_benefits: []
  });
  
  const [newRole, setNewRole] = useState("");
  const [newBenefit, setNewBenefit] = useState("");
  const [activeJobs, setActiveJobs] = useState([]);
  const [showJobSelector, setShowJobSelector] = useState(false);

  const COMMON_BENEFITS = [
    "Health Insurance", "Dental Insurance", "Vision Insurance", "401(k) / Retirement Plan",
    "Paid Time Off (PTO)", "Remote Work", "Hybrid Work", "Flexible Hours",
    "Parental Leave", "Life Insurance", "Stock Options / Equity", "Performance Bonus",
    "Professional Development", "Tuition Reimbursement", "Gym / Wellness Stipend",
    "Free Meals / Snacks", "Commuter Benefits", "Mental Health Support",
    "Disability Insurance", "Relocation Assistance", "Company Retreats",
    "Unlimited PTO", "Sabbatical Leave", "Pet-Friendly Office",
    "Child Care Assistance", "Home Office Stipend", "Paid Volunteering Days"
  ];

  useEffect(() => {
    fetchProfile();
    fetchActiveJobs();
  }, []);

  const fetchActiveJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/recruiter`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setActiveJobs(data.filter(job => job.status === "active"));
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/profile/recruiter`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          company_name: data.company_name || "",
          company_description: data.company_description || "",
          company_website: data.company_website || "",
          company_size: data.company_size || "",
          industry: data.industry || "",
          location: data.location || "",
          phone: data.phone || "",
          hiring_for: data.hiring_for || [],
          company_logo: data.company_logo || "",
          company_benefits: data.company_benefits || []
        });
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.company_name || formData.company_name.trim() === '') {
      toast.error("Company name is required");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/profile/recruiter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditing(false);
        toast.success("Profile updated successfully!");
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const addHiringRole = () => {
    if (newRole.trim() && !formData.hiring_for.includes(newRole.trim())) {
      setFormData(prev => ({
        ...prev,
        hiring_for: [...prev.hiring_for, newRole.trim()]
      }));
      setNewRole("");
    }
  };

  const removeHiringRole = (role) => {
    setFormData(prev => ({
      ...prev,
      hiring_for: prev.hiring_for.filter(r => r !== role)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const companySizes = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-500 employees",
    "501-1000 employees",
    "1000+ employees"
  ];

  const industries = [
    "Technology",
    "Healthcare",
    "Finance",
    "Education",
    "Manufacturing",
    "Retail",
    "Consulting",
    "Media & Entertainment",
    "Real Estate",
    "Non-profit",
    "Other"
  ];

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />

      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">COMPANY PROFILE</h1>
              <p className="text-gray-400 text-sm sm:text-base">Manage your company information and hiring details</p>
            </div>
            {!editing ? (
              <Button
                onClick={() => setEditing(true)}
                className="bg-crimson hover:bg-crimson-hover text-white"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-4 h-4 mr-2" /> Edit Profile
              </Button>
            ) : (
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setEditing(false);
                    fetchProfile();
                  }}
                  variant="outline"
                  className="border-[#333] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-crimson hover:bg-crimson-hover text-white"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" /> Save Changes
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Details */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h3 className="font-heading text-lg font-bold mb-6">COMPANY DETAILS</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Company Name <span className="text-crimson">*</span></label>
                    {editing ? (
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                        className="bg-obsidian border-[#333] text-white"
                        placeholder="Enter company name (required)"
                        required
                        data-testid="company-name-input"
                      />
                    ) : (
                      <p className="text-white font-medium">{profile?.company_name || "Not specified"}</p>
                    )}
                    {editing && !formData.company_name && (
                      <p className="text-crimson text-xs mt-1">Company name is required for job postings</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Industry</label>
                    {editing ? (
                      <select
                        value={formData.industry}
                        onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                        className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white focus:border-crimson outline-none"
                        data-testid="industry-select"
                      >
                        <option value="">Select industry</option>
                        {industries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-white">{profile?.industry || "Not specified"}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Company Size</label>
                    {editing ? (
                      <select
                        value={formData.company_size}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_size: e.target.value }))}
                        className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white focus:border-crimson outline-none"
                        data-testid="company-size-select"
                      >
                        <option value="">Select size</option>
                        {companySizes.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-white">{profile?.company_size || "Not specified"}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Location</label>
                    {editing ? (
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        className="bg-obsidian border-[#333] text-white"
                        placeholder="e.g., New York, NY"
                        data-testid="location-input"
                      />
                    ) : (
                      <p className="text-white flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        {profile?.location || "Not specified"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Website</label>
                    {editing ? (
                      <Input
                        value={formData.company_website}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_website: e.target.value }))}
                        className="bg-obsidian border-[#333] text-white"
                        placeholder="https://yourcompany.com"
                        data-testid="website-input"
                      />
                    ) : (
                      <p className="text-white flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-500" />
                        {profile?.company_website ? (
                          <a href={profile.company_website.startsWith("http") ? profile.company_website : `https://${profile.company_website}`} target="_blank" rel="noopener noreferrer" className="text-crimson hover:underline">
                            {profile.company_website}
                          </a>
                        ) : "Not specified"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">About Company</label>
                    {editing ? (
                      <textarea
                        value={formData.company_description}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_description: e.target.value }))}
                        className="w-full px-4 py-3 bg-obsidian border border-[#333] text-white focus:border-crimson outline-none resize-none"
                        rows={4}
                        placeholder="Describe your company, culture, and what makes it a great place to work..."
                        data-testid="description-input"
                      />
                    ) : (
                      <p className="text-gray-300 whitespace-pre-wrap">{profile?.company_description || "No description provided"}</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Hiring For */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">CURRENTLY HIRING FOR</h3>
                
                {/* Active Job Postings */}
                {activeJobs.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-400 mb-2">Active Job Postings:</p>
                    <div className="flex flex-wrap gap-2">
                      {activeJobs.map((job) => (
                        <Link
                          key={job.job_id}
                          to={`/jobs/${job.job_id}`}
                          className="px-3 py-1 bg-green-500/10 border border-green-500/30 text-green-400 text-sm flex items-center gap-2 hover:bg-green-500/20 transition-colors"
                        >
                          <Briefcase className="w-3 h-3" />
                          {job.title}
                          {job.applicants?.length > 0 && (
                            <span className="bg-green-500/20 px-1.5 py-0.5 text-xs rounded">
                              {job.applicants.length}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual roles - only show in edit mode */}
                {editing && (
                  <>
                    <p className="text-sm text-gray-400 mb-2">Additional Roles (manual):</p>
                    <div className="flex gap-2 mb-4">
                      <Input
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addHiringRole();
                          }
                        }}
                        className="bg-obsidian border-[#333] text-white flex-1"
                        placeholder="Add a role (e.g., Senior Developer)"
                        data-testid="add-role-input"
                      />
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          addHiringRole();
                        }}
                        className="bg-crimson hover:bg-crimson-hover text-white"
                        data-testid="add-role-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}

                <div className="flex flex-wrap gap-2">
                  {formData.hiring_for.length > 0 ? (
                    formData.hiring_for.map((role, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-crimson/10 border border-crimson/30 text-crimson text-sm flex items-center gap-2"
                      >
                        {role}
                        {editing && (
                          <button 
                            type="button"
                            onClick={() => removeHiringRole(role)} 
                            className="hover:text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    activeJobs.length === 0 && (
                      <p className="text-gray-500">No active jobs - post a job to get started!</p>
                    )
                  )}
                </div>
              </motion.div>

              {/* Employee Benefits */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                data-testid="benefits-section"
              >
                <h3 className="font-heading text-lg font-bold mb-4">EMPLOYEE BENEFITS</h3>
                <p className="text-xs text-gray-500 mb-4">Highlight benefits to attract top talent</p>

                {editing && (
                  <div className="mb-4 space-y-3">
                    {/* Dropdown of common benefits */}
                    <select
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val && !formData.company_benefits.includes(val)) {
                          setFormData(prev => ({ ...prev, company_benefits: [...prev.company_benefits, val] }));
                        }
                      }}
                      className="w-full bg-obsidian border border-[#333] px-3 py-2.5 text-sm text-white focus:border-crimson focus:outline-none"
                      data-testid="benefits-dropdown"
                    >
                      <option value="">Select a benefit...</option>
                      {COMMON_BENEFITS.filter(b => !formData.company_benefits.includes(b)).map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>

                    {/* Manual entry */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newBenefit}
                        onChange={(e) => setNewBenefit(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (newBenefit.trim() && !formData.company_benefits.includes(newBenefit.trim())) {
                              setFormData(prev => ({ ...prev, company_benefits: [...prev.company_benefits, newBenefit.trim()] }));
                              setNewBenefit("");
                            }
                          }
                        }}
                        placeholder="Type a custom benefit..."
                        className="flex-1 bg-obsidian border border-[#333] px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-crimson focus:outline-none"
                        data-testid="benefits-manual-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (newBenefit.trim() && !formData.company_benefits.includes(newBenefit.trim())) {
                            setFormData(prev => ({ ...prev, company_benefits: [...prev.company_benefits, newBenefit.trim()] }));
                            setNewBenefit("");
                          }
                        }}
                        className="bg-crimson/20 border border-crimson/30 text-crimson hover:bg-crimson hover:text-white"
                        data-testid="add-benefit-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {formData.company_benefits.length > 0 ? (
                    formData.company_benefits.map((benefit, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm flex items-center gap-2"
                        data-testid={`benefit-tag-${index}`}
                      >
                        {benefit}
                        {editing && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, company_benefits: prev.company_benefits.filter(b => b !== benefit) }))}
                            className="hover:text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No benefits listed yet{editing ? " — add some to attract talent!" : ""}</p>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Company Logo Card */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">COMPANY LOGO</h3>
                <div className="flex flex-col items-center gap-4">
                  <ProfilePictureUpload
                    currentImage={formData.company_logo || user?.picture}
                    uploadType="company"
                    size="large"
                    onUploadSuccess={(url) => {
                      setFormData(prev => ({ ...prev, company_logo: url }));
                      toast.success("Company logo uploaded!");
                    }}
                  />
                  <p className="text-gray-500 text-xs text-center">Click to upload your company logo</p>
                </div>
              </motion.div>

              {/* User Info Card */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-heading text-sm font-bold mb-4 text-gray-400">YOUR PROFILE</h3>
                <div className="flex items-center gap-4 mb-4">
                  <ProfilePictureUpload
                    currentImage={user?.picture}
                    uploadType="profile"
                    size="medium"
                    onUploadSuccess={(url) => {
                      // Update user context
                      setUser(prev => ({ ...prev, picture: url }));
                    }}
                  />
                  <div>
                    <p className="text-white font-medium">{user?.name}</p>
                    <p className="text-gray-500 text-sm">{user?.email}</p>
                  </div>
                </div>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">CONTACT</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-300">{user?.email}</span>
                  </div>
                  {editing ? (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="bg-obsidian border-[#333] text-white flex-1 text-sm"
                        placeholder="Phone number"
                        data-testid="phone-input"
                      />
                    </div>
                  ) : (
                    profile?.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-300">{profile.phone}</span>
                      </div>
                    )
                  )}
                </div>
              </motion.div>

              {/* Profile Completion */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">PROFILE STRENGTH</h3>
                
                {(() => {
                  const fields = [
                    { key: "company_name", label: "Company Name" },
                    { key: "industry", label: "Industry" },
                    { key: "company_size", label: "Company Size" },
                    { key: "location", label: "Location" },
                    { key: "company_description", label: "Description" },
                    { key: "company_website", label: "Website" }
                  ];
                  const completed = fields.filter(f => profile?.[f.key]).length;
                  const percentage = Math.round((completed / fields.length) * 100);
                  
                  return (
                    <>
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-400">Completion</span>
                          <span className="text-crimson font-bold">{percentage}%</span>
                        </div>
                        <div className="h-2 bg-[#1a1a1a] overflow-hidden">
                          <div 
                            className="h-full bg-crimson transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {fields.map(field => (
                          <div key={field.key} className="flex items-center gap-2 text-sm">
                            {profile?.[field.key] ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <X className="w-4 h-4 text-gray-600" />
                            )}
                            <span className={profile?.[field.key] ? "text-gray-400" : "text-gray-600"}>
                              {field.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RecruiterProfile;
